import { useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Globe, Server, Code2, TrendingUp, ArrowRight, Phone, Sparkles, HelpCircle, Newspaper, BookOpen, User, Calendar, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import logo from "@/assets/logo.webp";
import portrait from "@/assets/portrait.webp";

const serviceCategories = [
  {
    title: "De basis",
    description: "De basis voor je online aanwezigheid",
    icon: Server,
    color: "from-primary to-secondary",
    services: [
      { name: "Domeinnamen", href: "/diensten/domeinnamen" },
      { name: "Hosting", href: "/diensten/hosting" },
      { name: "WordPress Hosting", href: "/diensten/wordpress-hosting" },
      { name: "Zakelijke E-mail", href: "/diensten/zakelijke-email" },
      { name: "Google Workspace", href: "/diensten/google-workspace" },
    ],
  },
  {
    title: "Zichtbaarheid",
    description: "Websites en webshops die resultaat leveren",
    icon: Code2,
    color: "from-secondary to-primary",
    services: [
      { name: "Website bouwen", href: "/diensten/websites-bouwen" },
      { name: "Website per maand", href: "/diensten/website-per-maand", isNew: true },
      { name: "Webshop bouwen", href: "/diensten/webshops-bouwen" },
      { name: "Website onderhoud", href: "/diensten/website-onderhoud" },
    ],
  },
  {
    title: "Groei",
    description: "Meer bezoekers, meer klanten",
    icon: TrendingUp,
    color: "from-primary to-secondary",
    services: [
      { name: "SEO", href: "/diensten/seo" },
      { name: "SEA", href: "/diensten/sea" },
      { name: "AI-optimalisatie", href: "/diensten/ai-optimalisatie" },
    ],
  },
];

const infoLinks = [
  { name: "Over mij", href: "/over-mij", icon: User, description: "Wie ben ik?" },
  { name: "Reviews", href: "/reviews", icon: Sparkles, description: "Wat klanten zeggen" },
  { name: "AI Assistent", href: "/online-sale-specialist-assistent", icon: HelpCircle, description: "Stel direct je vraag" },
  { name: "Tools", href: "/tools", icon: Wrench, description: "Gratis online tools" },
  { name: "Nieuws", href: "/nieuws", icon: Newspaper, description: "Laatste updates" },
  { name: "Blog", href: "/blog", icon: BookOpen, description: "Tips en inzichten" },
];

const navigation: Array<{ name: string; href: string; hasDropdown?: "services" | "info" }> = [
  { name: "Home", href: "/" },
  { name: "Informatie", href: "#", hasDropdown: "info" },
  { name: "Diensten", href: "/diensten", hasDropdown: "services" },
  { name: "Projecten", href: "/projecten" },
  { name: "Contact", href: "/contact" },
];

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileInfoOpen, setMobileInfoOpen] = useState(false);
  const servicesCloseTimer = useRef<number | null>(null);
  const servicesTriggerHovered = useRef(false);
  const servicesMenuHovered = useRef(false);
  const infoCloseTimer = useRef<number | null>(null);
  const infoTriggerHovered = useRef(false);
  const infoMenuHovered = useRef(false);
  const location = useLocation();

  const clearServicesCloseTimer = () => {
    if (servicesCloseTimer.current) {
      window.clearTimeout(servicesCloseTimer.current);
      servicesCloseTimer.current = null;
    }
  };

  const openServicesMenu = () => {
    clearServicesCloseTimer();
    setServicesOpen(true);
  };

  const scheduleCloseServicesMenu = () => {
    clearServicesCloseTimer();
    servicesCloseTimer.current = window.setTimeout(() => {
      if (!servicesTriggerHovered.current && !servicesMenuHovered.current) {
        setServicesOpen(false);
      }
      servicesCloseTimer.current = null;
    }, 220);
  };

  const clearInfoCloseTimer = () => {
    if (infoCloseTimer.current) {
      window.clearTimeout(infoCloseTimer.current);
      infoCloseTimer.current = null;
    }
  };

  const openInfoMenu = () => {
    clearInfoCloseTimer();
    setInfoOpen(true);
  };

  const scheduleCloseInfoMenu = () => {
    clearInfoCloseTimer();
    infoCloseTimer.current = window.setTimeout(() => {
      if (!infoTriggerHovered.current && !infoMenuHovered.current) {
        setInfoOpen(false);
      }
      infoCloseTimer.current = null;
    }, 220);
  };

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    if (href === "#") return false;
    return location.pathname.startsWith(href);
  };

  const isInfoActive = () => {
    return infoLinks.some(link => location.pathname.startsWith(link.href));
  };

  return (
    <header className="bg-background/95 backdrop-blur-sm border-b border-border">
      <nav className="container-wide" aria-label="Hoofdnavigatie">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={logo} 
              alt="Online Sale Specialist" 
              className="h-8 lg:h-10 w-auto" 
              width={221}
              height={40}
              sizes="(max-width: 1024px) 178px, 221px"
              decoding="async"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center gap-5 2xl:gap-8">
            {navigation.map((item) => (
              <div key={item.name} className="relative">
                {item.hasDropdown === "services" ? (
                  <div
                    className="relative"
                    onMouseEnter={() => {
                      servicesTriggerHovered.current = true;
                      openServicesMenu();
                    }}
                    onMouseLeave={() => {
                      servicesTriggerHovered.current = false;
                      scheduleCloseServicesMenu();
                    }}
                  >
                    <Link
                      to={item.href}
                      className={`inline-flex items-center gap-1 text-sm font-medium transition-colors hover:text-foreground ${
                        isActive(item.href) ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {item.name}
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`} />
                    </Link>

                    {/* Services Mega Menu */}
                    <div 
                      className={`fixed left-1/2 -translate-x-1/2 top-[72px] w-[900px] transition-all duration-200 ${
                        servicesOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-2 pointer-events-none"
                      }`}
                      onMouseEnter={() => {
                        servicesMenuHovered.current = true;
                        openServicesMenu();
                      }}
                      onMouseLeave={() => {
                        servicesMenuHovered.current = false;
                        scheduleCloseServicesMenu();
                      }}
                    >
                      <div className="absolute -top-6 left-0 right-0 h-6" />
                      <div className="bg-background border border-border rounded-2xl shadow-lg overflow-hidden w-full" style={{ boxShadow: 'var(--shadow-lg)' }}>
                        <div className="grid grid-cols-3 divide-x divide-border">
                          {serviceCategories.map((category) => (
                            <div key={category.title} className="p-6 hover:bg-muted/30 transition-colors">
                              <div className="flex items-start gap-3 mb-4">
                                <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center flex-shrink-0`}>
                                  <category.icon className="h-5 w-5 text-primary-foreground" />
                                </div>
                                <div>
                                  <h4 className="font-semibold text-foreground">{category.title}</h4>
                                  <p className="text-xs text-muted-foreground mt-0.5">{category.description}</p>
                                </div>
                              </div>
                              <div className="space-y-0.5">
                                {category.services.map((service) => (
                                  <Link
                                    key={service.href}
                                    to={service.href}
                                    className="group flex items-center gap-2 px-3 py-2 -mx-3 rounded-lg hover:bg-muted transition-colors"
                                  >
                                    <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                                      {service.name}
                                    </span>
                                    {service.isNew && (
                                      <Badge variant="success" className="text-[10px] px-1.5 py-0 h-4">
                                        Nieuw
                                      </Badge>
                                    )}
                                    <ArrowRight className="h-3 w-3 text-muted-foreground/0 group-hover:text-muted-foreground group-hover:translate-x-0.5 transition-all ml-auto" />
                                  </Link>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="bg-muted/50 px-6 py-4 flex items-center justify-between border-t border-border">
                          <Link 
                            to="/online-sale-specialist-assistent" 
                            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <HelpCircle className="h-4 w-4 text-primary" />
                            <span>Snel antwoord nodig? <span className="font-medium text-foreground">Vraag de AI Assistent</span></span>
                          </Link>
                          <Link 
                            to="/diensten" 
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                          >
                            Alle diensten
                            <ArrowRight className="h-4 w-4" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : item.hasDropdown === "info" ? (
                  <div
                    className="relative"
                    onMouseEnter={() => {
                      infoTriggerHovered.current = true;
                      openInfoMenu();
                    }}
                    onMouseLeave={() => {
                      infoTriggerHovered.current = false;
                      scheduleCloseInfoMenu();
                    }}
                  >
                    <button
                      className={`inline-flex items-center gap-1 text-sm font-medium transition-colors hover:text-foreground ${
                        isInfoActive() ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {item.name}
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${infoOpen ? "rotate-180" : ""}`} />
                    </button>

                    {/* Info Dropdown */}
                    <div 
                      className={`fixed left-1/2 -translate-x-1/2 top-[72px] w-[620px] transition-all duration-200 ${
                        infoOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-2 pointer-events-none"
                      }`}
                      onMouseEnter={() => {
                        infoMenuHovered.current = true;
                        openInfoMenu();
                      }}
                      onMouseLeave={() => {
                        infoMenuHovered.current = false;
                        scheduleCloseInfoMenu();
                      }}
                    >
                      <div className="absolute -top-6 left-0 right-0 h-6" />
                      <div className="bg-background border border-border rounded-2xl shadow-lg overflow-hidden w-full" style={{ boxShadow: 'var(--shadow-lg)' }}>
                        <div className="grid grid-cols-[1fr_240px] divide-x divide-border">
                          {/* Links */}
                          <div className="p-6">
                            <div className="space-y-1">
                              {infoLinks.map((link) => (
                                <Link
                                  key={link.href}
                                  to={link.href}
                                  className="group flex items-center gap-3 px-3 py-3 -mx-3 rounded-lg hover:bg-muted transition-colors"
                                >
                                  <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                    <link.icon className="h-4 w-4 text-primary" />
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium text-foreground">{link.name}</span>
                                    <p className="text-xs text-muted-foreground">{link.description}</p>
                                  </div>
                                  <ArrowRight className="h-3 w-3 text-muted-foreground/0 group-hover:text-muted-foreground group-hover:translate-x-0.5 transition-all ml-auto" />
                                </Link>
                              ))}
                            </div>
                          </div>

                          {/* Contact block */}
                          <div className="p-6 bg-muted/30 flex flex-col items-center text-center">
                            <img 
                              src={portrait} 
                              alt="Steven - Online Sale Specialist" 
                              className="h-24 w-24 rounded-full object-cover object-top mb-4 border-2 border-border"
                            />
                            <h4 className="font-semibold text-foreground text-sm">Steven</h4>
                            <p className="text-xs text-muted-foreground mt-1 mb-4">
                              Online Sale Specialist
                            </p>
                            <Button asChild size="sm" className="w-full">
                              <Link to="/kennismaking" className="inline-flex items-center gap-2">
                                <Calendar className="h-3.5 w-3.5" />
                                Plan een gesprek
                              </Link>
                            </Button>
                            <a
                              href="tel:+31592887788"
                              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mt-3"
                            >
                              <Phone className="h-3 w-3" />
                              0592 88 77 88
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`text-sm font-medium transition-colors hover:text-foreground ${
                      isActive(item.href) ? "text-foreground" : "text-muted-foreground"
                    }`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Phone + CTA */}
          <div className="hidden xl:flex items-center gap-5">
            <a
              href="tel:+31592887788"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-accent transition-colors"
            >
              <span className="flex items-center justify-center h-8 w-8 rounded-full bg-primary/10">
                <Phone className="h-4 w-4" />
              </span>
              0592 88 77 88
            </a>
            <Button asChild size="default">
              <Link to="/offerte-aanvragen">Offerte aanvragen</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="xl:hidden p-2 text-foreground"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="xl:hidden bg-background border-t border-border max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="container-wide py-6 space-y-4">
            {navigation.map((item) => (
              <div key={item.name}>
                {item.hasDropdown === "services" ? (
                  <>
                    <button
                      onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                      className={`flex items-center justify-between w-full py-2 text-base font-medium ${
                        isActive(item.href) ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {item.name}
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileServicesOpen ? "rotate-180" : ""}`} />
                    </button>
                    {mobileServicesOpen && (
                      <div className="space-y-6 mt-4 pb-2">
                        {serviceCategories.map((category) => (
                          <div key={category.title}>
                            <div className="flex items-center gap-2.5 mb-3">
                              <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${category.color} flex items-center justify-center`}>
                                <category.icon className="h-4 w-4 text-primary-foreground" />
                              </div>
                              <div>
                                <span className="text-sm font-semibold text-foreground">{category.title}</span>
                                <p className="text-xs text-muted-foreground">{category.description}</p>
                              </div>
                            </div>
                            <div className="pl-10 space-y-1">
                              {category.services.map((service) => (
                                <Link
                                  key={service.href}
                                  to={service.href}
                                  className="block py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  {service.name}
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))}
                        <Link
                          to="/diensten"
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Alle diensten bekijken
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </div>
                    )}
                  </>
                ) : item.hasDropdown === "info" ? (
                  <>
                    <button
                      onClick={() => setMobileInfoOpen(!mobileInfoOpen)}
                      className={`flex items-center justify-between w-full py-2 text-base font-medium ${
                        isInfoActive() ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {item.name}
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileInfoOpen ? "rotate-180" : ""}`} />
                    </button>
                    {mobileInfoOpen && (
                      <div className="space-y-1 mt-2 pb-2">
                        {infoLinks.map((link) => (
                          <Link
                            key={link.href}
                            to={link.href}
                            className="flex items-center gap-3 py-2 pl-2"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <link.icon className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <span className="text-sm font-medium text-foreground">{link.name}</span>
                              <p className="text-xs text-muted-foreground">{link.description}</p>
                            </div>
                          </Link>
                        ))}
                        <div className="mt-4 pt-4 border-t border-border">
                          <div className="flex items-center gap-3 pl-2">
                            <img 
                              src={portrait} 
                              alt="Steven" 
                              className="h-10 w-10 rounded-full object-cover object-top border border-border"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-foreground">Plan een kennismaking</p>
                              <p className="text-xs text-muted-foreground">Gratis en vrijblijvend</p>
                            </div>
                            <Button asChild size="sm">
                              <Link to="/kennismaking" onClick={() => setMobileMenuOpen(false)}>
                                <Calendar className="h-3.5 w-3.5 mr-1.5" />
                                Plan gesprek
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={`block py-2 text-base font-medium ${
                      isActive(item.href) ? "text-foreground" : "text-muted-foreground"
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
            <div className="pt-4">
              <Button asChild className="w-full">
                <Link to="/offerte-aanvragen" onClick={() => setMobileMenuOpen(false)}>
                  Offerte aanvragen
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
